import React from 'react';
import { motion } from 'framer-motion';
import {
    Sprout,
    ArrowRight,
    ChevronRight,
    Construction,
    Utensils,
    HeartHandshake,
    Globe
} from 'lucide-react';
import kithulgalaviha from '../assets/kithulgalaviha.jpeg';
import kithulgala from '../assets/kithulgala.jpg';
import dansala from '../assets/dansala.jpg';
import bg from '../assets/annual.jpg';

export default function OngoingProjects() {
    const darkGreen = "#1A2E20";
    const goldAccent = "#D4AF37";
    const creamBg = "#FDFBF7";
    const bgPattern = "https://www.transparenttextures.com/patterns/natural-paper.png";

    const fadeIn = {
        initial: { opacity: 0, y: 30 },
        whileInView: { opacity: 1, y: 0 },
        viewport: { once: true, margin: "-50px" },
        transition: { duration: 0.8, ease: "easeOut" }
    };

    const activeProjects = [
        {
            title: "Kithulgala Sri Sumanawiwekaramaya",
            category: "Temple Restoration",
            description: "A comprehensive project safeguarding and enhancing the revered Siri Sumanawiwekaramaya. Our work includes developing the pagoda, building a Dharma Shala, and landscaping to maintain a sanctuary of tranquility.",
            image: kithulgalaviha,
            icon: <Construction size={24} className="text-[#B89561]" />
        },
        {
            title: "Annual June 'Dansala'",
            category: "Community Service",
            description: "On June 10th every year, we organize a free food stall (Noodles & Cottage Cheese) for the local community, keeping the spirit of selfless Buddhist hospitality alive for travelers and devotees.",
            image: dansala,
            icon: <Utensils size={24} className="text-[#B89561]" />
        }
    ];

    return (
        <div style={{ backgroundColor: creamBg, color: darkGreen }} className="min-h-screen font-sans selection:bg-[#B89561] selection:text-white relative overflow-hidden">
            <div className="absolute inset-0 pointer-events-none opacity-40 z-10" style={{ backgroundImage: `url(${bgPattern})` }}></div>

            {/* 1. HERO SECTION */}
            <section className="relative h-[60vh] md:h-[65vh] flex items-center overflow-hidden">
                <div className="absolute inset-0 z-0">
                    {/* Changed background source to the imported bg variable */}
                    <img src={bg} className="w-full h-full object-cover" alt="Ongoing Projects" />
                    <div className="absolute inset-0 bg-[#1A2E20]/50"></div>
                </div>

                <div className="relative z-20 max-w-7xl mx-auto px-4 sm:px-6 w-full pt-20">
                    <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.8 }}>
                        <div className="flex items-center gap-2 text-white/90 text-[9px] sm:text-[10px] tracking-[0.3em] sm:tracking-[0.4em] uppercase mb-4 sm:mb-6 font-bold">
                            <span>Projects</span>
                            <ChevronRight size={12} className="text-[#B89561]" />
                            <span className="text-white font-medium">Annual & Ongoing</span>
                        </div>
                        <h1 className="font-serif text-4xl sm:text-6xl md:text-8xl text-white leading-tight">
                            Seeds of <br/>
                            <span className="italic font-light text-[#D4AF37]">Compassion</span>
                        </h1>
                    </motion.div>
                </div>
            </section>

            {/* 2. INTRO */}
            <section className="relative py-16 sm:py-24 px-4 sm:px-6 z-20">
                <div className="max-w-4xl mx-auto text-center">
                    <motion.div {...fadeIn}>
                        <div className="flex justify-center mb-4 sm:mb-6">
                            <Globe color={goldAccent} size={36} className="sm:w-10 sm:h-10 md:w-12 md:h-12" />
                        </div>
                        <span style={{ color: goldAccent }} className="font-bold uppercase tracking-[0.3em] sm:tracking-[0.4em] text-[10px] sm:text-[11px] block mb-4 sm:mb-6">Our Continuous Journey</span>
                        <h2 className="font-serif text-3xl sm:text-5xl md:text-6xl text-[#1A2E20] mb-6 sm:mb-8 leading-tight">Fostering Peace for Countless Generations</h2>
                        <p className="text-gray-600 text-sm sm:text-base md:text-lg font-light leading-relaxed max-w-3xl mx-auto">
                            Boseth Wellness Foundation is committed to projects that breathe life into community and tradition. Whether through spiritual infrastructure or local nourishment, we ensure harmony is shared with those who need it most.
                        </p>
                    </motion.div>
                </div>
            </section>

            {/* 3. ACTIVE PROJECTS GRID */}
            <section className="relative pb-20 sm:pb-32 px-4 sm:px-6">
                <div className="max-w-7xl mx-auto">
                    <div className="grid md:grid-cols-2 gap-10 sm:gap-12 lg:gap-16">
                        {activeProjects.map((item, i) => (
                            <motion.div key={i} {...fadeIn} transition={{ delay: i * 0.1 }} className="group cursor-default max-w-[340px] sm:max-w-full mx-auto w-full">
                                <div className="relative aspect-[16/10] overflow-hidden rounded-[1.5rem] sm:rounded-[2rem] mb-4 sm:mb-8 shadow-2xl">
                                    <img src={item.image} alt={item.title} className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-110" />
                                    <div className="absolute top-4 left-4 sm:top-6 sm:left-6 bg-white/90 backdrop-blur-md px-3 py-1.5 sm:px-4 sm:py-2 rounded-full flex items-center gap-1.5 sm:gap-2">
                                        {item.icon}
                                        <span className="text-[8px] sm:text-[10px] font-bold uppercase tracking-widest text-[#1A2E20]">{item.category}</span>
                                    </div>
                                </div>
                                <div className="px-2 sm:px-4 text-center sm:text-left">
                                    <h3 className="font-serif text-xl sm:text-2xl md:text-3xl text-[#1A2E20] mb-2 sm:mb-4 group-hover:text-[#B89561] transition-colors duration-500">
                                        {item.title}
                                    </h3>
                                    <p className="text-gray-500 text-xs sm:text-sm md:text-base font-light leading-relaxed mb-4 sm:mb-6">
                                        {item.description}
                                    </p>
                                    <button className="flex items-center gap-2 text-[10px] sm:text-[11px] font-bold uppercase tracking-[0.25em] sm:tracking-[0.3em] text-[#B89561] hover:text-[#1A2E20] transition-colors mx-auto sm:mx-0">
                                        Project Status <ArrowRight size={14} />
                                    </button>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* 4. DONATION CTA */}
            <section className="relative py-16 sm:py-24 px-4 sm:px-6">
                <div className="max-w-6xl mx-auto">
                    <motion.div {...fadeIn} className="bg-[#1A2E20] rounded-[2rem] sm:rounded-[3rem] p-6 sm:p-12 md:p-16 flex flex-col md:flex-row justify-between items-center gap-8 sm:gap-10 shadow-2xl relative overflow-hidden">
                        <div className="absolute inset-0 opacity-10 pointer-events-none" style={{ backgroundImage: `url(${bgPattern})` }}></div>
                        <div className="relative z-10 text-center md:text-left">
                            <h2 className="font-serif text-2xl sm:text-4xl md:text-5xl text-white mb-3 sm:mb-4">Make a Sacred Impact</h2>
                            <p className="text-white/60 text-xs sm:text-sm md:text-lg font-light max-w-xl">
                                Your support empowers us to continue our community services and temple developments across the nation.
                            </p>
                        </div>
                        <button style={{ backgroundColor: goldAccent }} className="w-full sm:w-auto relative z-10 text-white px-8 sm:px-10 py-4 sm:py-5 rounded-full font-bold uppercase text-[11px] sm:text-[12px] tracking-[0.2em] hover:bg-white hover:text-[#1A2E20] transition-all duration-500 flex items-center justify-center gap-3 shadow-xl group">
                            Support Projects <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
                        </button>
                    </motion.div>
                </div>
            </section>
        </div>
    );
}